import React, { useState } from 'react'
import {
  Col,
  Form,
  FormInstance,
  Input,
  Radio,
  RadioChangeEvent,
  Row,
  Tabs,
} from 'antd'
import { MagnifyingGlass, PlusCircle, X } from '@phosphor-icons/react'
import { sendMessage, createChatRoom } from 'helper/Chat/ChatHelpers'
import { eventShatStore } from 'state/event/eventChat'
import { myStore } from 'state/store'
import { ChatMessageType, SendEmailPayload, SendSmsPayload } from 'types/chat'
import { useSendMessageNotification } from 'api/Notification'
import { useSendEmailToAttendees, useSendSmsToAttendees } from 'api/Contact/Contact'
import { failureMessage } from 'api/global'
import { serverTimestamp, Timestamp } from 'firebase/firestore'
import { useFindOrCreateChatRoom } from 'helper/Hooks/chatMessages'
import CustomSelectMenu from 'components/global/CustomSelectMenu'
import Attendees from 'pages/Events/Chats/Modals/ModalContnet/Attendees'
import { Person } from 'types/globalTypes'

const { TabPane } = Tabs

interface LeagueChatContentProps {
  form: FormInstance
  leagueId: string
  participants: Person[]
}

const LeagueChatContent: React.FC<LeagueChatContentProps> = ({
  form,
  leagueId,
  participants,
}) => {
  const chatRoomIds = useFindOrCreateChatRoom(leagueId)
  const senderId = localStorage.getItem('adminId')
  const [contactType, setContactType] = useState<'chat' | 'email' | 'sms'>('chat')
  const [recipientOpen, setRecipientOpen] = useState(false)
  const [search, setSearch] = useState('')

  const {
    selectedParticipants,
    setSelectedParticipants,
    selectedParticipantsData,
    setSelectedParticipantsData,
    selectedParticipantsEmails,
    setChatModalVisible,
    setSelectedChatRoomId,
  } = eventShatStore(store => ({
    selectedParticipants: store.selectedParticipants,
    setSelectedParticipants: store.setSelectedParticipants,
    selectedParticipantsData: store.selectedParticipantsData,
    setSelectedParticipantsData: store.setSelectedParticipantsData,
    selectedParticipantsEmails: store.selectedParticipantsEmails,
    setChatModalVisible: store.setChatModalVisible,
    setSelectedChatRoomId: store.setSelectedChatRoomId,
  }))

  const setModalBtnLoader = myStore(store => store.setModalBtnLoader)
  const { mutate: messageNotificationMutation } = useSendMessageNotification()
  const { mutate: sendEmailMutation } = useSendEmailToAttendees()
  const { mutate: sendSmsMutation } = useSendSmsToAttendees()

  const filterBySearch = (list: Person[]) =>
    search
      ? list.filter(p =>
          `${p.firstName} ${p.lastName}`.toLowerCase().includes(search.toLowerCase()),
        )
      : list

  const kids = filterBySearch(participants.filter(p => p.role === 'kid'))
  const guardians = filterBySearch(
    participants.filter(p => p.role === 'guardian' || p.role === 'authorizedAdult'),
  )
  const mentors = filterBySearch(participants.filter(p => p.role === 'mentor'))

  const handleRemoveRecipient = (id: string) => {
    setSelectedParticipants(selectedParticipants.filter(i => i !== id))
    setSelectedParticipantsData(selectedParticipantsData.filter(i => i._id !== id))
  }

  const menuItems = [
    {
      key: 'recipients',
      label: (
        <div>
          <div className="flex align-center gap-10px border-btm px-10px py-5px">
            <MagnifyingGlass size={20} color="#000" />
            <input
              type="text"
              className="w-full border-none"
              placeholder="Search..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              onClick={e => e.stopPropagation()}
            />
          </div>
          <div className="mt-10px">
            <Tabs defaultActiveKey="1">
              <TabPane tab="Attendees" key="1">
                <Attendees
                  participants={kids}
                  setOpen={setRecipientOpen}
                  contactType={contactType}
                />
              </TabPane>
              <TabPane tab="Guardians" key="2">
                <Attendees
                  participants={guardians}
                  setOpen={setRecipientOpen}
                  contactType={contactType}
                />
              </TabPane>
              <TabPane tab="Mentors" key="3">
                <Attendees
                  participants={mentors}
                  setOpen={setRecipientOpen}
                  contactType={contactType}
                />
              </TabPane>
            </Tabs>
          </div>
        </div>
      ),
    },
  ]

  async function createRoom(message: ChatMessageType) {
    setModalBtnLoader(true)
    try {
      const roomId = await createChatRoom(
        leagueId,
        'LEAGUECHATS',
        'League Chat',
        [...selectedParticipants, senderId],
        message,
        selectedParticipantsData,
      )
      setModalBtnLoader(false)
      setChatModalVisible(false)
      setSelectedChatRoomId(roomId)
    } catch {
      setModalBtnLoader(false)
      failureMessage('Failed to create chat room')
    }
  }

  const handleFinish = async (values: any) => {
    if (!selectedParticipants.length) {
      return failureMessage('Please select at least one recipient')
    }

    if (contactType === 'chat') {
      const existingRoom = chatRoomIds?.find(room =>
        selectedParticipants.every(id => room.data.memberIds.includes(id)),
      )
      const message: ChatMessageType = {
        messageText: values.message,
        messageType: 'Text',
        senderId,
        recieverId: selectedParticipants[0] || null,
        readByUsers: [senderId],
        time: serverTimestamp() as unknown as Timestamp,
      }
      if (existingRoom) {
        await sendMessage(existingRoom.id, senderId, selectedParticipants[0], 'Text', values.message, '', '', [senderId])
        setChatModalVisible(false)
        setSelectedChatRoomId(existingRoom.id)
      } else {
        await createRoom(message)
      }
      messageNotificationMutation({
        chatRoomId: existingRoom?.id || '',
        members: selectedParticipantsData.map(p => ({
          id: p._id,
          role: 'guardian',
          name: `${p.firstName} ${p.lastName}`,
        })),
        lastMessage: values.message,
        lastMessageType: 'Text',
        senderId,
        title: 'New Message',
      })
    } else if (contactType === 'email') {
      const payload: SendEmailPayload = {
        attendeesIds: selectedParticipants,
        ccRecipients: selectedParticipantsEmails,
        bccRecipients: selectedParticipantsEmails,
        subject: values.subject || 'Message from League Admin',
        text: values.message,
      }
      setModalBtnLoader(true)
      sendEmailMutation(payload, {
        onSuccess: () => { setModalBtnLoader(false); setChatModalVisible(false) },
        onError: () => { setModalBtnLoader(false); failureMessage('Failed to send email') },
      })
    } else if (contactType === 'sms') {
      const payload: SendSmsPayload = {
        attendeesIds: selectedParticipants,
        text: values.message,
      }
      setModalBtnLoader(true)
      sendSmsMutation({ payload }, {
        onSuccess: () => { setModalBtnLoader(false); setChatModalVisible(false) },
        onError: () => { setModalBtnLoader(false); failureMessage('Failed to send SMS') },
      })
    }
  }

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={{ contactVia: 'chat' }}
      onFinish={handleFinish}
    >
      <Row>
        <Col span={24}>
          <label className="input-label">Contact via</label>
          <Form.Item name="contactVia">
            <Radio.Group onChange={(e: RadioChangeEvent) => setContactType(e.target.value)}>
              <Radio value="email">Email</Radio>
              <Radio value="sms">SMS</Radio>
              <Radio value="chat">Chat</Radio>
            </Radio.Group>
          </Form.Item>
        </Col>
      </Row>

      <Row className="mt-10px">
        <Col span={24}>
          <label className="input-label">Recipients</label>
          <CustomSelectMenu
            open={recipientOpen}
            setOpen={setRecipientOpen}
            menuItems={menuItems}
          >
            <div
              className="w-full flex align-center justify-between gap-20px mt-5px border-1px radius-12 px-15px h-50px"
              onClick={() => setRecipientOpen(true)}
            >
              <div className="w-90p overflow-hidden">
                {!selectedParticipants.length ? (
                  <span className="text-14 text-gray">Select recipients</span>
                ) : (
                  <div className="flex align-center gap-10px">
                    {selectedParticipants.map(id => {
                      const person = participants.find(p => p._id === id)
                      return (
                        <div
                          key={id}
                          className="px-5px py-5px bg-grey radius-full flex align-center gap-10px"
                        >
                          <span className="text-space-nowrap text-14">
                            {person?.firstName}
                          </span>
                          <X
                            size={14}
                            color="#000"
                            className="pointer"
                            onClick={e => { e.stopPropagation(); handleRemoveRecipient(id) }}
                          />
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
              <PlusCircle size={20} color="#000" />
            </div>
          </CustomSelectMenu>
        </Col>
      </Row>

      {contactType === 'email' && (
        <Row className="mt-10px">
          <Col span={24}>
            <label className="input-label">Subject</label>
            <Form.Item name="subject">
              <Input placeholder="Email subject" className="br-12" />
            </Form.Item>
          </Col>
        </Row>
      )}

      <Row className="mt-10px">
        <Col span={24}>
          <label className="input-label required-input">Message</label>
          <Form.Item name="message" rules={[{ required: true, message: 'Message is required' }]}>
            <Input.TextArea rows={4} placeholder="Type your message..." className="br-12" />
          </Form.Item>
        </Col>
      </Row>
    </Form>
  )
}

export default LeagueChatContent
